import React from 'react';
import { BrowserStreamPanel, Button, Flex, Text } from '@chatsdet/ui';
import { CheckCircle, CircleNotch, X } from '@phosphor-icons/react';
import type { BrowserStreamState, ContextFindingEvent } from '@chatsdet/types';
import type { ApplicationContextItem, ApplicationContextSection } from '../types';
import { SECTION_LABELS, SECTION_ORDER } from '../types';

type Props = {
  browserStream: { state: BrowserStreamState; isConnected: boolean };
  findings: ContextFindingEvent['data'][];
  itemsFromDb: ApplicationContextItem[];
  onCancel: () => void;
  isCancelling: boolean;
};

export function BootstrapLiveView({ browserStream, findings, itemsFromDb, onCancel, isCancelling }: Props) {
  const allFindings = React.useMemo(() => {
    if (findings.length > 0) return findings;
    return itemsFromDb.map((item) => ({
      section: item.section,
      itemKey: item.itemKey,
      itemLabel: item.itemLabel,
      content: item.content,
      timestamp: item.createdAt,
    }));
  }, [findings, itemsFromDb]);

  const grouped = React.useMemo(() => {
    const result: Partial<Record<ApplicationContextSection, typeof allFindings>> = {};
    for (const finding of allFindings) {
      const section = finding.section as ApplicationContextSection;
      if (!result[section]) result[section] = [];
      result[section]!.push(finding);
    }
    return result;
  }, [allFindings]);

  const findingsEndRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    findingsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allFindings.length]);

  return (
    <div className="flex h-[calc(100vh-200px)] min-h-[500px]">
      {/* Left: Findings report (2/3) */}
      <div className="w-2/3 flex flex-col min-w-0 pr-6">
        <Flex align="center" justify="between" className="pb-3 mb-4 border-b border-border shrink-0">
          <Flex align="center" gap="sm">
            <CircleNotch size={14} className="text-amber-500 animate-spin" />
            <Text size="sm" weight="medium" className="text-foreground">
              Exploring application
            </Text>
            <Text size="sm" className="text-muted-foreground">
              {allFindings.length > 0 && `\u00b7 ${allFindings.length} found`}
            </Text>
          </Flex>
          <Button
            variant="ghost"
            size="sm"
            onPress={onCancel}
            isDisabled={isCancelling}
          >
            <X size={14} />
            Cancel
          </Button>
        </Flex>

        <div className="flex-1 overflow-y-auto">
          {allFindings.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <Text size="sm" className="text-muted-foreground">
                Waiting for findings...
              </Text>
            </div>
          ) : (
            <div className="space-y-5">
              {SECTION_ORDER.map((section) => {
                const items = grouped[section];
                if (!items?.length) return null;

                return (
                  <div key={section}>
                    <Text
                      size="xs"
                      weight="medium"
                      className="text-muted-foreground uppercase tracking-wider mb-2"
                    >
                      {SECTION_LABELS[section]}
                    </Text>
                    <div className="space-y-1.5">
                      {items.map((finding, idx) => (
                        <div key={`${finding.itemKey}-${idx}`} className="py-1.5">
                          <Flex align="start" gap="sm">
                            <CheckCircle
                              size={14}
                              className="text-green-500 shrink-0 mt-0.5"
                              weight="fill"
                            />
                            <div>
                              <Text size="sm" weight="medium" className="text-foreground">
                                {finding.itemLabel}
                              </Text>
                              <Text size="sm" className="text-muted-foreground">
                                {finding.content}
                              </Text>
                            </div>
                          </Flex>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              <div ref={findingsEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* Right: Browser stream (1/3) */}
      <div className="w-1/3 border-l border-border pl-4">
        <BrowserStreamPanel state={browserStream.state} className="h-full" />
      </div>
    </div>
  );
}
